import React, { useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useWorkWeeks } from './shared/useWorkWeeks';
import WorkWeekSelect from './shared/WorkWeekSelect';

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const toNumberOrNull = (v: unknown) =>
  v === null || v === undefined || v === '' ? null : Number(v);

const LocationsPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form] = Form.useForm();
  const { labelByCode: workWeekLabels } = useWorkWeeks();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.locations(),
    queryFn: () => hrmsService.listLocations(),
  });

  const { data: calendars = [] } = useQuery({
    queryKey: hrmsKeys.holidayCalendars(),
    queryFn: () => hrmsService.listHolidayCalendars(),
  });

  const calendarOptions = calendars.map((c: any) => ({
    value: c.id,
    label: c.calendar_name || c.name || c.calendar_code || 'Calendar',
  }));

  const calendarLabel = (id?: string) =>
    calendarOptions.find((o) => o.value === id)?.label || id || '—';

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ work_week: 'SUN_THU', is_active: true });
    setDrawerOpen(true);
  };

  const openEdit = (r: any) => {
    setEditing(r);
    form.setFieldsValue({
      location_code: r.location_code || r.code,
      location_name: r.location_name || r.name,
      address: r.address,
      latitude: toNumberOrNull(r.latitude),
      longitude: toNumberOrNull(r.longitude),
      permitted_radius_meters: r.permitted_radius_meters,
      work_week: r.work_week,
      holiday_calendar_id: r.holiday_calendar_id,
      is_active: r.is_active !== false,
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body = {
        location_code: v.location_code,
        location_name: v.location_name,
        code: v.location_code,
        name: v.location_name,
        address: v.address || null,
        latitude: toNumberOrNull(v.latitude),
        longitude: toNumberOrNull(v.longitude),
        permitted_radius_meters: toNumberOrNull(v.permitted_radius_meters),
        work_week: v.work_week,
        holiday_calendar_id: v.holiday_calendar_id || null,
        is_active: v.is_active,
      };
      if (editing) await hrmsService.updateLocation(editing.id, body);
      else await hrmsService.createLocation(body);
      message.success(editing ? 'Updated' : 'Created');
      setDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: hrmsKeys.locations() });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    }
  };

  const handleTemplate = async () => {
    try {
      saveBlob(await hrmsService.downloadLocationTemplateBlob(), 'locations-import-template.xlsx');
    } catch {
      message.error('Template download failed');
    }
  };

  const handleExport = async () => {
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      saveBlob(await hrmsService.exportLocationsExcelBlob(), `locations-${stamp}.xlsx`);
    } catch {
      message.error('Export failed');
    }
  };

  const importMutation = useMutation({
    mutationFn: (file: File) => hrmsService.importLocationsExcel(file),
    onSuccess: ({ created, updated, skipped }) => {
      message.success(
        `Import done: ${created} created, ${updated} updated` +
          (skipped ? `, ${skipped} blank row(s) skipped` : '')
      );
      void queryClient.invalidateQueries({ queryKey: hrmsKeys.locations() });
    },
    onError: (e: any) => {
      const errors: string[] | undefined = e?.response?.data?.data?.errors;
      if (errors?.length) setImportErrors(errors);
      else message.error(e?.response?.data?.message || e?.message || 'Import failed');
    },
  });

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) importMutation.mutate(file);
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Code',
      dataIndex: 'location_code',
      width: 120,
      render: (v, r) => v || r.code || '—',
    },
    {
      title: 'Name',
      dataIndex: 'location_name',
      render: (v, r) => v || r.name || '—',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: 'Geofence',
      key: 'geo',
      width: 230,
      render: (_, r) =>
        r.latitude != null && r.longitude != null ? (
          <Space size={4}>
            <EnvironmentOutlined />
            <Typography.Link
              href={`https://www.google.com/maps?q=${Number(r.latitude)},${Number(r.longitude)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {Number(r.latitude).toFixed(5)}, {Number(r.longitude).toFixed(5)}
            </Typography.Link>
            {r.permitted_radius_meters ? <Tag>{r.permitted_radius_meters} m</Tag> : null}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'Employees',
      dataIndex: 'employee_count',
      width: 110,
      render: (n: number, r) => (
        <Typography.Link onClick={() => navigate(`/hr/employees/location-assignment?location=${r.id}`)}>
          {n ?? 0}
        </Typography.Link>
      ),
    },
    {
      title: 'Work week',
      dataIndex: 'work_week',
      width: 140,
      render: (v: string) => workWeekLabels.get(v) || v || '—',
    },
    {
      title: 'Holiday calendar',
      dataIndex: 'holiday_calendar_id',
      ellipsis: true,
      render: (id: string) => calendarLabel(id),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => <Tag color={v !== false ? 'green' : 'default'}>{v !== false ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      width: 80,
      render: (_, r) => (
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Work locations"
          subtitle="Sites with address, check-in geofence, work week and holiday calendar"
          primaryActions={
            <Space wrap>
              <Button icon={<FileExcelOutlined />} onClick={handleTemplate}>
                Import template
              </Button>
              <Button
                icon={<UploadOutlined />}
                loading={importMutation.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                Import
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                Export
              </Button>
              <Button onClick={() => navigate('/hr/employees/location-assignment')}>
                Assign employees
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Add location
              </Button>
            </Space>
          }
        />
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={onFileChosen}
      />
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No locations yet" />
          ),
        }}
      />

      <Drawer
        title={editing ? 'Edit location' : 'New location'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        destroyOnClose
        extra={
          <Button type="primary" onClick={save}>
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="location_code" label="Code" rules={[{ required: true }]}>
            <Input disabled={Boolean(editing)} placeholder="DXB-HQ" />
          </Form.Item>
          <Form.Item name="location_name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Dubai Head Office" />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} placeholder="Building, street, area, city" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="latitude"
                label="Latitude"
                dependencies={['longitude']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: (_, v) =>
                      (v == null) === (getFieldValue('longitude') == null)
                        ? Promise.resolve()
                        : Promise.reject(new Error('Enter latitude and longitude together')),
                  }),
                ]}
              >
                <InputNumber min={-90} max={90} precision={7} step={0.0001} style={{ width: '100%' }} placeholder="25.1857000" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="longitude" label="Longitude" dependencies={['latitude']}>
                <InputNumber min={-180} max={180} precision={7} step={0.0001} style={{ width: '100%' }} placeholder="55.2723000" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="permitted_radius_meters"
            label="Permitted radius (meters)"
            extra="Distance from the point within which mobile check-in / check-out is allowed."
            dependencies={['latitude']}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, v) =>
                  v == null || getFieldValue('latitude') != null
                    ? Promise.resolve()
                    : Promise.reject(new Error('Set latitude and longitude first')),
              }),
            ]}
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="150" />
          </Form.Item>
          <Form.Item name="work_week" label="Work week" rules={[{ required: true }]}>
            <WorkWeekSelect />
          </Form.Item>
          <Form.Item name="holiday_calendar_id" label="Holiday calendar">
            <Select allowClear showSearch optionFilterProp="label" options={calendarOptions} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title="Import failed — nothing was imported"
        open={importErrors.length > 0}
        onCancel={() => setImportErrors([])}
        footer={<Button onClick={() => setImportErrors([])}>Close</Button>}
      >
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Fix these rows in the file and upload it again."
        />
        <ul style={{ maxHeight: 320, overflow: 'auto', paddingLeft: 18 }}>
          {importErrors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </Modal>
    </WorkspaceLayout>
  );
};

export default LocationsPage;
